import React, { useState } from 'react'
import { Elements, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js'

import "isomorphic-fetch"

// Apple Pay / Google Pay button.
//
// Deliberately owns its own <Elements> provider rather than sharing the card
// form's. The wallet needs deferred-intent mode (mode/amount/currency up
// front), and elements.submit() validates every element in its group -- so a
// shared provider would validate the empty card fields the moment someone taps
// Apple Pay, and fail every wallet payment. Two providers keeps the card path
// byte-for-byte as it was.
//
// paymentMethodTypes is pinned to card: Apple Pay and Google Pay are both card
// PaymentMethods, and this makes it impossible for Stripe to offer a
// redirect-based method (this account has Bancontact, EPS and Afterpay live).
// Booking creation is synchronous, so a redirect would strand the customer.
const appearance = {
	theme: 'stripe',
	variables: {
		borderRadius: '4px'
	}
}

// Kept minimal on purpose. Stripe throws an IntegrationError for any option it
// does not recognise, and that throw happens during render -- which unmounts
// the entire React tree, checkout included. Which wallets can appear is already
// constrained by paymentMethodTypes: ['card'] on the Elements group below.
//
// paymentMethods is the exception, and it is why the button did not appear on
// staging in desktop Chrome. Stripe hides Apple Pay on every non-Safari desktop
// browser, and hides Google Pay from anyone who has not set Google Pay up,
// unless each is asked for by name. Left on 'auto' the element reports no
// available payment methods and renders nothing at all -- silently, with no
// console error. 'always' does not force a wallet onto a platform that cannot
// take it: an unsupported browser or currency still shows nothing.
//
// link is off, and must stay off while deposit and split-the-bill are on offer.
// A Link PaymentMethod has type 'link' and no `card` member, so save_card's
// `payment_method[:card]` is nil, add_card raises on `card[:brand]`, and the
// rescue around it swallows that -- leaving a booking with a balance to collect
// and no stored card for Deposit::PaymentsJob, which ends in an automatic
// cancellation and refund.
const expressOptions = {
	buttonHeight: 48,
	paymentMethods: {
		applePay: 'always',
		googlePay: 'always',
		link: 'never'
	}
}

export const toPence = ( amount ) => Math.round( Math.abs( Number( amount ) ) * 100 )

// The wallet is an enhancement; the card form is the business. A throw anywhere
// inside Stripe's element -- an option it dislikes, a script that fails to load,
// a browser quirk -- must not unmount the checkout around it. Without this, an
// error here takes the whole React island down and the customer sees an empty
// page where the payment form should be. That happened on staging.
class ExpressBoundary extends React.Component {

	constructor( props ) {
		super( props )
		this.state = { failed: false }
	}

	static getDerivedStateFromError() {
		return { failed: true }
	}

	componentDidCatch( error ) {
		if ( window.Sentry && window.Sentry.captureException ) {
			window.Sentry.captureException( error )
		}
	}

	render() {
		return this.state.failed ? null : this.props.children
	}
}

const ExpressCheckout = ({ stripePromise, amount, token, config, getPaymentData, additionalFormData, onError }) => {

	const amountInPence = toPence( amount )

	// Stripe rejects Elements creation with a zero amount, and the checkout
	// starts at 0 until a payment option is chosen.
	if ( !amountInPence || amountInPence <= 0 ) {
		return null
	}

	return (
		<ExpressBoundary>
			<Elements
				stripe={stripePromise}
				options={{
					mode: 'payment',
					amount: amountInPence,
					currency: 'gbp',
					setupFutureUsage: 'off_session',
					paymentMethodTypes: ['card'],
					appearance: appearance
				}}
			>
				<ExpressButton
					token={token}
					config={config}
					getPaymentData={getPaymentData}
					additionalFormData={additionalFormData}
					onError={onError}
				/>
			</Elements>
		</ExpressBoundary>
	)
}

export default ExpressCheckout


const ExpressButton = ({ token, config, getPaymentData, additionalFormData, onError }) => {

	const stripe = useStripe()
	const elements = useElements()

	// Hidden until Stripe tells us a wallet is actually available. Without this
	// there is a blank gap above the card form on every browser that has none.
	const [ available, setAvailable ] = useState( false )

	const onReady = ({ availablePaymentMethods }) => {
		setAvailable( !!availablePaymentMethods )

		// Which wallets the browser was actually offered. When no button appears
		// there is otherwise nothing to separate a device that cannot take a
		// wallet from an account setting, a domain that is not registered, or a
		// broken integration -- and the ones worth diagnosing are real customer
		// devices, which are on live. So: on outside live, and on in live when
		// ?wallet_debug=1 is on the URL.
		const asked = typeof window !== 'undefined' &&
			window.location.search.indexOf( 'wallet_debug=1' ) !== -1

		if ( ( config.mode !== 'live' || asked ) && window.console ) {
			console.log( '[express] available wallets:', availablePaymentMethods || 'none' )
			console.log( '[express] stripe mode:', config.mode, '| host:', window.location.host )
		}
	}

	const postParams = ( extra ) => {
		const paymentData = Object.assign( {}, getPaymentData(), extra )
		paymentData['payment_method'] = 'express'

		return {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				payment: paymentData,
				authenticity_token: token
			})
		}
	}

	// Fires before the wallet sheet opens. Anything wrong with the rest of the
	// form has to be reported now -- once the sheet is up the customer cannot
	// see the page behind it.
	const onClick = async ({ resolve, reject }) => {
		onError( false )

		try {
			// Same shape the card form validates: a map of field name to
			// { valid, name }. The wallet has no card fields of its own, so
			// this is the only validation that applies to it.
			const fields = await additionalFormData()
			const errors = []

			Object.keys( fields || {} ).forEach(( key ) => {
				const field = fields[key]
				if ( field && field.valid === false ) {
					errors.push( field.name )
				}
			})

			if ( errors.length > 0 ) {
				onError( 'Please correct the following fields: ' + errors.join(', ') )
				reject()
				return
			}
		} catch ( err ) {
			onError( err.message )
			reject()
			return
		}

		resolve()
	}

	// The event carries paymentFailed. Every early return below has to call it, or the wallet
	// sheet stays open with a spinner until Stripe gives up and reports REQUEST_TIMEOUT -- so a
	// perfectly clear server refusal ("Unable to book via this process", when an admin tries to
	// book) reached the customer as a timeout, and the real reason was only visible in the log.
	// A reason and message close the sheet and show the message inside it.
	const onConfirm = async ( event ) => {

		onError( false )

		const fail = ( message ) => {
			onError( message )
			if ( event && typeof event.paymentFailed === 'function' ) {
				event.paymentFailed({ reason: 'fail', message: message || undefined })
			}
		}

		if ( !stripe || !elements ) {
			fail( 'Sorry, the payment form is still loading. Please try again in a moment.' )
			return
		}

		try {
			const submit = await elements.submit()
			if ( submit.error ) {
				fail( submit.error.message )
				return
			}

			// The server creates the booking, the payment row and an unconfirmed
			// PaymentIntent, and hands back the client secret.
			const created = await fetch( config.paths.post, postParams({}) )
			const response = await created.json()

			if ( response.error ) {
				fail( typeof response.error === 'object' ? response.error.message : response.error )
				return
			}

			const { payment_intent_client_secret, bid, pid } = response.data || {}

			if ( !payment_intent_client_secret || !bid || !pid ) {
				fail( 'Sorry, we could not start that payment. Please try again.' )
				return
			}

			// redirect: 'if_required' rather than a return_url. With card-only
			// payment methods nothing needs to redirect, so this resolves here
			// and we finish server-side on the same request -- no return route,
			// no session to survive a round trip through Stripe.
			const confirmed = await stripe.confirmPayment({
				elements,
				clientSecret: payment_intent_client_secret,
				redirect: 'if_required'
			})

			if ( confirmed.error ) {
				fail( confirmed.error.message )
				return
			}

			// Same second leg the 3DS card flow uses.
			const finalised = await fetch( config.paths.post, postParams({
				payment_intent_id: confirmed.paymentIntent.id,
				bid: bid,
				pid: pid
			}) )
			const result = await finalised.json()

			if ( result.error ) {
				fail( typeof result.error === 'object' ? result.error.message : result.error )
				return
			}

			const url = result.data && result.data.path ? result.data.path : config.paths.success
			window.location.replace( url )

		} catch ( err ) {
			fail( err.message )
		}
	}

	return (
		<div className="express-checkout" style={{ display: available ? 'block' : 'none' }}>
			<ExpressCheckoutElement
				options={expressOptions}
				onReady={onReady}
				onClick={onClick}
				onConfirm={onConfirm}
				onCancel={() => onError( false )}
			/>
			<div className="express-checkout__divider"><span>or pay by card</span></div>
		</div>
	)
}
